(() => {
  function clcEmitContextChanged(productInfo) {
    if (!productInfo) return;
    productInfo.dispatchEvent(new CustomEvent('clc:context-changed', { bubbles: false }));
  }

  function clcFormatWithDelimiters(cents, precision, thousands, decimal) {
    const t = thousands ?? ',';
    const d = decimal ?? '.';
    if (Number.isNaN(cents) || cents == null) return '0';
    const num = (Number(cents) / 100).toFixed(precision);
    const parts = num.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${t}`);
    const centsPart = parts[1] ? d + parts[1] : '';
    return dollars + centsPart;
  }

  /** Same placeholder rules as Shopify's legacy formatMoney (shop.money_format). */
  function clcFormatMoneyWithTemplate(cents, formatString) {
    const fmt = String(formatString);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholderRegex);
    if (!match) return null;
    const key = match[1];
    let value = '';
    switch (key) {
      case 'amount':
        value = clcFormatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = clcFormatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = clcFormatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = clcFormatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = clcFormatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = clcFormatWithDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = clcFormatWithDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(placeholderRegex, value);
  }

  function clcFormatMoneyCents(cents, format) {
    let n = Math.round(Number(cents));

    const centsPart = n % 100;

    if (centsPart < 10) {
      n = n - centsPart;
    }
    if (Number.isNaN(n)) return '';
    const fmt = format && String(format).trim();
    if (fmt && /\{\{\s*\w+\s*\}\}/.test(fmt)) {
      const templated = clcFormatMoneyWithTemplate(n, fmt);
      if (templated != null) return templated;
    }
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function' && fmt) {
      return window.Shopify.formatMoney(n, fmt);
    }
    const code =
      (typeof window.Shopify !== 'undefined' && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
      }).format(n / 100);
    } catch {
      return (n / 100).toFixed(2);
    }
  }

  function clcNormalizeSellingPlanId(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
    const s = String(raw);
    const tail = s.match(/(\d+)\s*$/);
    if (tail) return parseInt(tail[1], 10);
    const n = parseInt(s, 10);
    return Number.isNaN(n) ? null : n;
  }

  function clcParseSubscriptionPricing(sectionId) {
    if (!sectionId) return null;
    const el = document.getElementById(`ClcSubscriptionPricing-${sectionId}`);
    if (!el || !el.textContent.trim()) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function clcPlanIdForVariant(pricing, variantId, allocationIndex) {
    if (!pricing?.variants || variantId == null || variantId === '') return null;
    const row = pricing.variants[String(variantId)];
    if (!row) return null;
    const idx =
      typeof allocationIndex === 'number' && !Number.isNaN(allocationIndex) ? allocationIndex : 0;
    const alloc = Array.isArray(row.allocations) ? row.allocations[idx] : null;
    if (!alloc) return null;
    return clcNormalizeSellingPlanId(alloc.planId);
  }

  function clcSyncFormSellingPlan(productInfo, pricing) {
    const form = productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    form.querySelectorAll('input[name="selling_plan"][data-clc-selling-plan]').forEach((el) => el.remove());

    if (!pricing?.variants) return;

    const idx = 0;
    const pi = productInfo;
    const flavorBtn = pi.querySelector('clc-custom-variants .clc-option-button.is-active[data-variant-id]');
    let activeVariantId = flavorBtn?.dataset.variantId;
    if (!activeVariantId) {
      activeVariantId = form.querySelector('input[name="id"]')?.value;
    }
    const qtyBtn = pi.querySelector('clc-custom-quantity-option .clc-option-button.is-active');
    const qtySource = qtyBtn?.closest('clc-custom-quantity-option');
    const varietyPackProductId = qtySource?.dataset?.varietyPackProductId;
    const flavorProductId = flavorBtn?.dataset.productId || pi.dataset.productId;
    const isVarietyBundle =
      varietyPackProductId &&
      flavorProductId &&
      String(flavorProductId) === String(varietyPackProductId) &&
      qtySource;

    if (isVarietyBundle) {
      return;
    }

    const planId = clcPlanIdForVariant(pricing, activeVariantId, idx);
    if (planId == null) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.setAttribute('data-clc-selling-plan', 'true');
    input.value = String(planId);
    form.appendChild(input);
  }

  function clcRcBundleRandomSegment() {
    return Math.random().toString(36).substring(2, 10);
  }

  function clcReadRcBundleConfig(productInfo) {
    const w = productInfo?.querySelector('clc-subscription-widget');
    if (!w) return null;
    const vid = w.dataset.rcBundleVariantId;
    const cid = w.dataset.rcBundleCollectionId;
    const parent = w.dataset.rcBundleParent;
    const variant = w.dataset.rcBundleVariant;
    if (!vid || !cid || parent == null || String(parent).trim() === '') return null;
    return {
      variantId: String(vid).trim(),
      collectionId: String(cid).trim(),
      parent: String(parent),
      variant: String(variant).trim(),
    };
  }

  function clcBuildRcBundleProperties(cfg) {
    if (!cfg) return null;
    const seg = clcRcBundleRandomSegment();
    return {
      _rc_bundle: `${seg}:${cfg.variantId}`,
      _rc_bundle_parent: cfg.parent,
      _rc_bundle_variant: cfg.variant,
      _rc_bundle_collection_id: cfg.collectionId,
    };
  }

  function clcParseSubscriptionDiscountPercent(el) {
    if (!el?.dataset) return 0;
    let raw = String(el.dataset.subscriptionDiscountPercent ?? '').trim();
    if (!raw) return 0;
    raw = raw.replace(',', '.');
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return 0;
    return Math.min(100, Math.max(0, n));
  }

  function clcEnsureFormRcBundleOnSubmit(productInfo) {
    const form = productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || form.dataset.clcRcBundleSubmitBound === 'true') return;
    form.dataset.clcRcBundleSubmitBound = 'true';
    form.addEventListener(
      'submit',
      () => {
        form.querySelectorAll('input[data-clc-rc-bundle-prop]').forEach((el) => el.remove());
        const cfg = clcReadRcBundleConfig(productInfo);
        if (!cfg) return;
        const props = clcBuildRcBundleProperties(cfg);
        if (!props) return;
        Object.entries(props).forEach(([key, val]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = String(val);
          input.setAttribute('data-clc-rc-bundle-prop', 'true');
          form.appendChild(input);
        });
      },
      true
    );
  }

  class ClcCustomVariants extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.onDocPointerDown = (e) => {
        const dropdown = this.querySelector('.clc-variant-dropdown');
        if (!dropdown || !dropdown.classList.contains('is-open')) return;
        if (dropdown.contains(e.target)) return;
        this.closeDropdown();
      };
      document.addEventListener('pointerdown', this.onDocPointerDown, true);
      this.initializeSelection();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('pointerdown', this.onDocPointerDown, true);
    }

    initializeSelection() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;
      const selectedButton =
        this.querySelector('.clc-option-button.is-active[data-variant-id]') ||
        this.querySelector('.clc-option-button[data-variant-id]');
      if (!selectedButton) return;
      this.setActiveButton(selectedButton);
      this.updateAddToCartVariant(productInfo, selectedButton.dataset.variantId);
      this.updateGalleryImage(productInfo, selectedButton.dataset.imageUrl);
      if (selectedButton.dataset.productId) {
        productInfo.dataset.productId = selectedButton.dataset.productId;
      }
      clcEmitContextChanged(productInfo);
    }

    closeDropdown() {
      const dropdown = this.querySelector('.clc-variant-dropdown');
      dropdown?.classList.remove('is-open');
      const panel = this.querySelector('.clc-variant-dropdown__panel');
      const trigger = this.querySelector('.clc-variant-dropdown__trigger');
      panel?.setAttribute('hidden', '');
      trigger?.setAttribute('aria-expanded', 'false');
    }

    toggleDropdown() {
      const panel = this.querySelector('.clc-variant-dropdown__panel');
      const trigger = this.querySelector('.clc-variant-dropdown__trigger');
      const dropdown = this.querySelector('.clc-variant-dropdown');
      if (!panel || !trigger) return;
      const willOpen = panel.hasAttribute('hidden');
      if (willOpen) {
        panel.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        dropdown?.classList.add('is-open');
      } else {
        this.closeDropdown();
      }
    }

    updateAddToCartVariant(productInfo, variantId) {
      if (!variantId) return;
      const form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;
      const variantInput = form.querySelector('input[name="id"]');
      if (!variantInput) return;
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateGalleryImage(productInfo, imageUrl) {
      if (!imageUrl) return;
      const mediaImage = productInfo.querySelector('.product__media-item.is-active img, .product__media-item img');
      if (mediaImage) {
        mediaImage.src = imageUrl;
        mediaImage.removeAttribute('srcset');
      }

      const thumbImage = productInfo.querySelector('.thumbnail[aria-current="true"] img, .thumbnail img');
      if (thumbImage) {
        thumbImage.src = imageUrl;
        thumbImage.removeAttribute('srcset');
      }
    }

    onClick = (event) => {
      const trigger = event.target.closest('.clc-variant-dropdown__trigger');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        this.toggleDropdown();
        return;
      }

      const button = event.target.closest('[data-variant-id]');
      if (!button) return;

      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      this.setActiveButton(button);
      this.updateAddToCartVariant(productInfo, button.dataset.variantId);
      this.updateGalleryImage(productInfo, button.dataset.imageUrl);

      if (button.dataset.productId) {
        productInfo.dataset.productId = button.dataset.productId;
      }
      clcEmitContextChanged(productInfo);
    };

    setActiveButton(activeButton) {
      this.querySelectorAll('.clc-option-button').forEach((button) => {
        const on = button === activeButton;
        button.classList.toggle('is-active', on);
        if (button.hasAttribute('aria-selected')) {
          button.setAttribute('aria-selected', on ? 'true' : 'false');
        }
      });
      const label = this.querySelector('.clc-variant-dropdown__trigger-label');
      const triggerBadge = this.querySelector('.clc-variant-dropdown__trigger-badge');
      if (label && activeButton && !activeButton.classList.contains('clc-variant-dropdown__trigger')) {
        const optionLabel = activeButton.querySelector('.clc-variant-dropdown__option-label');
        const optionBadge = activeButton.querySelector('.clc-variant-badge--inline-select');
        label.textContent = optionLabel ? optionLabel.textContent.trim() : activeButton.textContent.trim();
        if (triggerBadge) {
          const badgeText = optionBadge ? optionBadge.textContent.trim() : '';
          if (badgeText) {
            triggerBadge.textContent = badgeText;
            triggerBadge.hidden = false;
          } else {
            triggerBadge.textContent = '';
            triggerBadge.hidden = true;
          }
        }
      }
      this.closeDropdown();
    }
  }

  class ClcCustomQuantityOption extends HTMLElement {
    connectedCallback() {
      const productInfo = this.closest('product-info');
      const button = this.querySelector('.clc-option-button');
      if (!productInfo || !button) return;
      clcEnsureFormRcBundleOnSubmit(productInfo);
      this.initializeBundleSubmitHandler(productInfo);

      button.addEventListener('click', () => {
        productInfo
          .querySelectorAll('clc-custom-quantity-option .clc-option-button')
          .forEach((qtyButton) => qtyButton.classList.remove('is-active'));
        button.classList.add('is-active');
        this.applyQuantity(productInfo, button.dataset.quantity);
        clcEmitContextChanged(productInfo);
      });

      if (button.classList.contains('is-active')) {
        this.applyQuantity(productInfo, button.dataset.quantity);
      }
      clcEmitContextChanged(productInfo);
    }

    initializeBundleSubmitHandler(productInfo) {
      if (productInfo.dataset.clcBundleSubmitReady === 'true') return;
      const form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;

      productInfo.dataset.clcBundleSubmitReady = 'true';
      form.addEventListener(
        'submit',
        async (event) => {
        if (!productInfo.querySelector('.clc-custom-quantity-wrap')) return;

        const activeQtyButton = productInfo.querySelector(
          'clc-custom-quantity-option .clc-option-button.is-active'
        );
        const bundleSource = activeQtyButton?.closest('clc-custom-quantity-option');
        if (!bundleSource) return;

        const varietyPackProductId = bundleSource.dataset.varietyPackProductId;
        if (!varietyPackProductId) return;

        const activeFlavorBtn = productInfo.querySelector(
          'clc-custom-variants .clc-option-button.is-active[data-product-id]'
        );
        const flavorProductId =
          activeFlavorBtn?.dataset.productId || productInfo.dataset.productId;
        if (
          !flavorProductId ||
          String(flavorProductId) !== String(varietyPackProductId)
        ) {
          return;
        }

        const bundleItems = [];
        for (let i = 1; i <= 5; i += 1) {
          const variantId = bundleSource.getAttribute(`data-bundle-item-${i}-variant-id`);
          const qty = Number(bundleSource.getAttribute(`data-bundle-item-${i}-qty`) || 0);
          if (variantId && qty > 0) bundleItems.push({ id: Number(variantId), qty });
        }
        if (!bundleItems.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const productFormEl = form.closest('product-form');
        const checkoutRedirect = productFormEl?.dataset.checkoutRedirect === 'true';
        const submitBtn = productFormEl?.querySelector('[type="submit"]');
        const spinner = productFormEl?.querySelector('.loading__spinner');

        const sectionId = productInfo.dataset.section;
        const pricing = clcParseSubscriptionPricing(sectionId);

        const rcCfg = clcReadRcBundleConfig(productInfo);
        const rcProps = rcCfg ? clcBuildRcBundleProperties(rcCfg) : null;

        const items = bundleItems.map((item) => {
          const line = {
            id: Number(item.id),
            quantity: Math.max(1, item.qty),
          };
          if (pricing) {
            const planId = clcPlanIdForVariant(pricing, String(item.id), 0);
            if (planId != null) line.selling_plan = planId;
          }
          if (rcProps) {
            line.properties = { ...rcProps };
          }
          return line;
        });

        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = { items };
        if (
          !checkoutRedirect &&
          cart &&
          typeof cart.getSectionsToRender === 'function'
        ) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
        }
        spinner?.classList.remove('hidden');

        const addUrl = window.routes?.cart_add_url ? `${window.routes.cart_add_url}.js` : '/cart/add.js';
        try {
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });

          let parsedState;
          try {
            parsedState = await response.json();
          } catch {
            return;
          }

          if (!response.ok || parsedState.status) {
            return;
          }

          if (checkoutRedirect) {
            const startMarker =
              typeof window.CartPerformance?.createStartingMarker === 'function'
                ? window.CartPerformance.createStartingMarker('add:wait-for-subscribers')
                : null;
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: String(items[0].id),
              cartData: parsedState,
            });
            if (startMarker && typeof window.CartPerformance?.measureFromMarker === 'function') {
              window.CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }
            window.location.href = window.routes?.checkout_url || '/checkout';
            return;
          }

          if (cart) {
            if (typeof cart.setActiveElement === 'function') {
              cart.setActiveElement(document.activeElement);
            }
            const startMarker =
              typeof window.CartPerformance?.createStartingMarker === 'function'
                ? window.CartPerformance.createStartingMarker('add:wait-for-subscribers')
                : null;
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: String(items[0].id),
              cartData: parsedState,
            });
            if (startMarker && typeof window.CartPerformance?.measureFromMarker === 'function') {
              window.CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }
            if (typeof window.CartPerformance?.measure === 'function') {
              window.CartPerformance.measure('add:paint-updated-sections', () => {
                cart.renderContents(parsedState);
              });
            } else {
              cart.renderContents(parsedState);
            }
            if (cart.classList.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        } finally {
          submitBtn?.classList.remove('loading');
          spinner?.classList.add('hidden');
          submitBtn?.removeAttribute('aria-disabled');
        }
        },
        true
      );
    }

    applyQuantity(productInfo, quantity) {
      const form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;

      let quantityInput = form.querySelector('input[name="quantity"]');
      if (!quantityInput) {
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        form.appendChild(quantityInput);
      }

      quantityInput.value = Number(quantity) > 0 ? quantity : '1';
    }
  }

  class ClcSubscriptionWidget extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.sectionId = this.dataset.sectionId;
      this.boundUpdate = () => this.updateDisplay();
      this.pricing = null;
      this.productInfo?.addEventListener('clc:context-changed', this.boundUpdate);
      this.productInfo?.addEventListener('change', this.boundUpdate);
      this.loadPricing();
      clcEnsureFormRcBundleOnSubmit(this.productInfo);
      this.updateDisplay();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('clc:context-changed', this.boundUpdate);
      this.productInfo?.removeEventListener('change', this.boundUpdate);
    }

    loadPricing() {
      const el = document.getElementById(`ClcSubscriptionPricing-${this.sectionId}`);
      const fallbackFormat = this.dataset.moneyFormat || '';
      if (!el || !el.textContent.trim()) {
        this.pricing = { variants: {}, moneyFormat: fallbackFormat };
        return;
      }
      try {
        this.pricing = JSON.parse(el.textContent);
      } catch {
        this.pricing = { variants: {}, moneyFormat: fallbackFormat };
      }
      if (!this.pricing.moneyFormat && fallbackFormat) {
        this.pricing.moneyFormat = fallbackFormat;
      }
    }

    getVariantRow(variantId) {
      if (!variantId || !this.pricing?.variants) return null;
      return this.pricing.variants[String(variantId)];
    }

    updateDisplay() {
      const priceEl = this.querySelector('[data-clc-sub-price]');
      const cmpEl = this.querySelector('[data-clc-sub-compare]');
      const badgeEl = this.querySelector('[data-clc-sub-badge]');
      if (!priceEl || !this.productInfo) return;

      const pi = this.productInfo;
      const flavorBtn = pi.querySelector('clc-custom-variants .clc-option-button.is-active[data-variant-id]');
      const activeVariantId = flavorBtn?.dataset.variantId;
      const flavorProductId = flavorBtn?.dataset.productId || pi.dataset.productId;
      const qtyBtn = pi.querySelector('clc-custom-quantity-option .clc-option-button.is-active');
      const qtySource = qtyBtn?.closest('clc-custom-quantity-option');
      const varietyPackProductId = qtySource?.dataset?.varietyPackProductId;
      const fmt = this.pricing.moneyFormat || '';
      const idx = 0;
      const discountPct = clcParseSubscriptionDiscountPercent(qtySource);

      const isVarietyBundle =
        varietyPackProductId &&
        flavorProductId &&
        String(flavorProductId) === String(varietyPackProductId) &&
        qtySource;

      let mainCents = 0;
      let strikeCents = 0;
      let usedSubscriptionAllocation = false;

      if (isVarietyBundle) {
        const lines = [];
        for (let i = 1; i <= 5; i += 1) {
          const vid = qtySource.getAttribute(`data-bundle-item-${i}-variant-id`);
          const q = Number(qtySource.getAttribute(`data-bundle-item-${i}-qty`) || 0);
          if (!vid || q <= 0) continue;
          const row = this.getVariantRow(vid);
          if (!row) continue;
          const alloc = Array.isArray(row.allocations) ? row.allocations[idx] : null;
          lines.push({ row, q, alloc });
        }
        const everyAlloc = lines.length > 0 && lines.every((l) => l.alloc);
        if (everyAlloc) {
          usedSubscriptionAllocation = true;
          lines.forEach(({ alloc, q }) => {
            mainCents += alloc.price * q;
            const sc = alloc.compareAt && alloc.compareAt > 0 ? alloc.compareAt : alloc.price;
            strikeCents += sc * q;
          });
        } else {
          lines.forEach(({ row, q }) => {
            mainCents += row.price * q;
            const base = row.compareAt && row.compareAt > 0 ? row.compareAt : row.price;
            strikeCents += base * q;
          });
        }
      } else {
        const formEl = pi.querySelector('form[data-type="add-to-cart-form"]');
        const vid = activeVariantId || formEl?.querySelector('input[name="id"]')?.value;
        if (vid) {
          const activeQtyBtn = pi.querySelector('clc-custom-quantity-option .clc-option-button.is-active');
          const qtyWrap = activeQtyBtn?.closest('clc-custom-quantity-option');
          const qtyRaw = Number(
            activeQtyBtn?.dataset?.quantity ||
              qtyWrap?.dataset?.quantity ||
              formEl?.querySelector('input[name="quantity"]')?.value ||
              1
          );
          const lineQty = Math.max(1, Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1);

          const row = this.getVariantRow(vid);
          if (row) {
            const alloc = Array.isArray(row.allocations) ? row.allocations[idx] : null;
            if (alloc) {
              usedSubscriptionAllocation = true;
              const unitMain = alloc.price;
              let unitStrike = 0;
              if (alloc.compareAt && alloc.compareAt > alloc.price) {
                unitStrike = alloc.compareAt;
              } else if (row.compareAt && row.compareAt > row.price) {
                unitStrike = row.compareAt;
              }
              mainCents = unitMain * lineQty;
              strikeCents = unitStrike > unitMain ? unitStrike * lineQty : 0;
            } else {
              mainCents = row.price * lineQty;
              strikeCents =
                row.compareAt && row.compareAt > row.price ? row.compareAt * lineQty : 0;
            }
          }
        }
      }

      if (usedSubscriptionAllocation && discountPct > 0 && mainCents > 0) {
        const baseSub = mainCents;
        mainCents = Math.round((baseSub * (100 - discountPct)) / 100);
        strikeCents = Math.max(strikeCents || 0, baseSub);
      }

      priceEl.textContent = mainCents > 0 ? clcFormatMoneyCents(mainCents, fmt) : '—';
      if (cmpEl) {
        if (strikeCents > mainCents && mainCents > 0) {
          cmpEl.textContent = clcFormatMoneyCents(strikeCents, fmt);
          cmpEl.hidden = false;
        } else {
          cmpEl.textContent = '';
          cmpEl.hidden = true;
        }
      }
      if (badgeEl) {
        if (strikeCents > mainCents && mainCents > 0) {
          const pct = Math.round((1 - mainCents / strikeCents) * 100);
          if (pct > 0 && pct < 100) {
            badgeEl.textContent = `${pct}% OFF`;
            badgeEl.hidden = false;
          } else {
            badgeEl.hidden = true;
          }
        } else {
          badgeEl.hidden = true;
        }
      }

      clcSyncFormSellingPlan(this.productInfo, this.pricing);
    }
  }

  if (!customElements.get('clc-custom-variants')) {
    customElements.define('clc-custom-variants', ClcCustomVariants);
  }
  if (!customElements.get('clc-custom-quantity-option')) {
    customElements.define('clc-custom-quantity-option', ClcCustomQuantityOption);
  }
  if (!customElements.get('clc-subscription-widget')) {
    customElements.define('clc-subscription-widget', ClcSubscriptionWidget);
  }
})();
